/**
 * The width below which a list stops being a list.
 *
 * Every track in a `crm-list-head` / `crm-list-row` grid is an `fr`, and `fr`
 * tracks share out whatever width there is however little that is. On a phone
 * that did not produce a narrow list - it produced seven unreadable columns,
 * with the heads overlapping into one word and every cell cut to two letters.
 * Worse, the rows were then the widest thing on the page, so the whole document
 * scrolled sideways and anything laid out against the viewport was clipped at
 * the right edge with no scrollbar to explain it.
 *
 * The card scrolls instead, below a floor derived from the tracks themselves.
 * Derived rather than written down twice: a list that gains a column gains the
 * room for it, and nobody has to remember to raise a number in a second file.
 *
 * See `.crm-card:has(> .crm-list-head)` in index.css for the rule this feeds.
 */
import type { CSSProperties } from "react";

/** What one flexible track needs before its content stops being readable -
 *  about a status pill, a formatted date, or a truncated company name. */
const FLEX_TRACK = 130;

/** `crm-list-head`'s horizontal padding, and the gap between two tracks. */
const SIDE_PADDING = 52;
const GAP = 14;

/**
 * A list of three short columns should not demand a scrollbar on a tablet, but
 * it should not claim to fit a phone either. Nothing goes below this.
 */
const MIN_FLOOR = 560;

/** Split a `grid-template-columns` string into tracks, keeping `minmax(a, b)`
 *  whole - its comma and space are inside the parentheses. */
function splitTracks(cols: string): string[] {
  const tracks: string[] = [];
  let depth = 0;
  let current = "";
  for (const ch of cols) {
    if (ch === "(") depth += 1;
    if (ch === ")") depth -= 1;
    if (ch === " " && depth === 0) {
      if (current) tracks.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  if (current) tracks.push(current);
  return tracks;
}

/**
 * How wide one track has to be. A fixed track asks for exactly what it says; a
 * `minmax(120px, 2fr)` asks for its own minimum when that is bigger than the
 * flexible default; everything else takes the default.
 */
function trackWidth(track: string): number {
  const fixed = /^(\d+(?:\.\d+)?)px$/.exec(track);
  if (fixed) return Number(fixed[1]);
  const floor = /^minmax\((\d+(?:\.\d+)?)px/.exec(track);
  if (floor) return Math.max(Number(floor[1]), FLEX_TRACK);
  return FLEX_TRACK;
}

/** The floor a `COLS` string implies, in pixels. */
export function listFloor(cols: string): number {
  const tracks = splitTracks(cols);
  if (tracks.length === 0) return MIN_FLOOR;
  const content = tracks.reduce((sum, track) => sum + trackWidth(track), 0);
  return Math.max(MIN_FLOOR, Math.round(content + GAP * (tracks.length - 1) + SIDE_PADDING));
}

/**
 * The style for the `crm-card` around a list. The variable inherits down to the
 * head, the rows and the footer, which is why it goes on the card and not on
 * any one of them.
 */
export function listCard(cols: string): CSSProperties {
  return { "--crm-list-min": `${listFloor(cols)}px` } as CSSProperties;
}
